<template>
    <div class="flex flex-col items-center justify-center mb-4">
        <div class="relative group cursor-pointer" @click="triggerFileUpload">
            <img v-if="avatar" :src="avatar" 
                :class="['avatar-img shadow-lg', avatarSize, roundedClass]" />
            <img v-else :src="avatarURL || '/imgs/image_default.png' + '?' + timeStamp()" @error="setDefaultAvatar"
                :class="['avatar-img', roundedClass, avatarSize, error ? 'border-4 border-rose-600' : 'border-4']" />
            
            <!-- Overlay com ícone que aparece no hover -->
            <div :class="['absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300', roundedClass]">
                <svg :class="[iconSize, 'text-white mb-1']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                <span v-if="showText && size !== 'sm' && size !== 'md'" :class="['text-white text-xs font-medium', textSize]">
                    {{ textLabel }}
                </span>
            </div>
        </div>
        <input type="file" ref="fileInput" @change="updateAvatar" accept="image/*" hidden>
    </div>
</template>


<script>
export default {
    props: {
        avatarURL: {
            type: [String, Object],
            default: null,
        },
        error: {
            type: String,
            default: '',
        },
        size: {
            type: String,
            default: 'xl',
        },
        rounded: {
            type: Boolean,
            default: true,
        },
        imageIndex: {
            type: Number, // Novo parâmetro para identificar o índice da imagem
            required: true,
        },
        showText: {
            type: Boolean,
            default: true,
        },
        textLabel: {
            type: String,
            default: 'Alterar foto',
        },
    },

    data() {
        return {
            avatar: null, // Gerencia o estado interno do avatar exibido
        };
    },

    computed: {
        avatarSize() {
            switch (this.size) {
                case 'sm':
                    return 'h-8 w-8';
                case 'md':
                    return 'h-12 w-12';
                case 'lg':
                    return 'h-16 w-16';
                case 'xl':
                    return 'h-20 w-20';
                case '2xl':
                    return 'h-24 w-24';
                case '3xl':
                    return 'h-32 w-32';
                case '4xl':
                    return 'h-40 w-40';
                case '5xl':
                    return 'h-[200px] w-[220px]';

                default:
                    return 'h-16 w-16';
            }
        },

        roundedClass() {
            return this.rounded ? 'rounded-full' : 'rounded-lg'; // Define as classes CSS
        },

        iconSize() {
            switch (this.size) {
                case 'sm':
                    return 'w-3 h-3';
                case 'md':
                    return 'w-4 h-4';
                case 'lg':
                    return 'w-5 h-5';
                case 'xl':
                    return 'w-6 h-6';
                case '2xl':
                    return 'w-7 h-7';
                case '3xl':
                    return 'w-8 h-8';
                case '4xl':
                    return 'w-10 h-10';
                case '5xl':
                    return 'w-12 h-12';

                default:
                    return 'w-6 h-6';
            }
        },

        textSize() {
            switch (this.size) {
                case 'sm':
                    return 'text-xs';
                case 'md':
                    return 'text-xs';
                case 'lg':
                    return 'text-sm';
                case 'xl':
                    return 'text-sm';
                case '2xl':
                    return 'text-base';
                case '3xl':
                    return 'text-lg';
                case '4xl':
                    return 'text-xl';
                case '5xl':
                    return 'text-2xl';

                default:
                    return 'text-sm';
            }
        }
    },

    methods: {
        timeStamp() {
            return new Date().getTime();
        },

        triggerFileUpload() {
            this.$refs.fileInput.click();
        },

        updateAvatar(event) {
            const file = event.target.files[0];
            if (file) {
                this.cropImage(file);
            }
        },

        setDefaultAvatar(event) {
            event.target.src = 'https://app.wbuy.ai/imgs/image_default.png'; // Define a imagem padrão
        },

        cropImage(file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const resizeImage = (targetSize) => {
                        const canvas = document.createElement('canvas');
                        canvas.width = targetSize;
                        canvas.height = targetSize;
                        const ctx = canvas.getContext('2d');
                        const scale = Math.max(targetSize / img.width, targetSize / img.height);
                        const xOffset = (targetSize - img.width * scale) / 2;
                        const yOffset = (targetSize - img.height * scale) / 2;
                        ctx.drawImage(img, xOffset, yOffset, img.width * scale, img.height * scale);
                        return canvas.toDataURL('image/jpeg');
                    };

                    const thumbnailBase64 = resizeImage(100);
                    const largeBase64 = resizeImage(250);

                    this.avatar = thumbnailBase64; // Atualiza a miniatura localmente
                    this.$emit('avatarChanged', {
                        thumbnail: thumbnailBase64,
                        large: largeBase64,
                        index: this.imageIndex, // Emite o índice junto com as imagens
                    });
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        },

    },

    watch: {
        avatarURL: {
            handler(newVal) {
                if (!this.avatar || this.avatar !== newVal) {
                    this.avatar = newVal; // Atualiza o estado interno sempre que avatarURL mudar
                }
            },
            immediate: true, // Executa imediatamente após a montagem do componente
        },
    },
};
</script>

<style scoped>
.avatar-img {
    cursor: pointer;
    object-fit: cover;
    /* Garante que a imagem cubra o espaço sem distorcer */
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease, opacity 0.3s ease;
}

.group:hover .avatar-img {
    transform: scale(1.05);
    opacity: 0.8;
}

.rounded-full {
    border-radius: 50%;
    /* Totalmente arredondado */
}

.rounded-md {
    border-radius: 8px;
    /* Arredondado suave */
}

.rounded-lg {
    border-radius: 12px;
    /* Arredondado médio */
}

/* Melhora o posicionamento do ícone para diferentes tamanhos */
.group svg {
    transition: transform 0.3s ease;
}

.group:hover svg {
    transform: scale(1.1);
}

/* Adiciona um efeito de pulsação sutil no ícone */
.group:hover svg {
    animation: pulse 1.5s ease-in-out infinite;
}

/* Estilo para o texto no hover */
.group span {
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    transition: opacity 0.3s ease;
}

/* Melhora a legibilidade para avatares pequenos */
.group:hover span {
    opacity: 1;
}

/* Ajusta o padding para avatares pequenos quando há texto */
.group:hover .avatar-img {
    filter: brightness(0.8);
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.7;
    }
}
</style>
